/**
 * Pure data layer for the top-bar command palette. It stays free of React/DOM
 * so the fast unit project can exercise it without DOM startup; the SPA component
 * (`layout/CommandPalette.tsx`) wires these helpers to query data, keyboard
 * handling, and navigation.
 *
 * Conversations and files are deliberately *not* indexed here: the Hub has no
 * cheap client-side index for either. Callers may extend the index only with the
 * workspace entity kinds declared below. Conversation text is instead reached by
 * handing the typed query to the rail's server-side search — see
 * {@link conversationSearchGroup} — so the palette stays one search concept
 * without holding a transcript index.
 */

import { workspaceConversationSearchPath } from "./portal-route.js";
import { skillDisplayName } from "./text-format.js";

export type CommandKind =
  | "page"
  | "dashboard"
  | "application"
  | "workflow"
  | "blueprint"
  | "specialist"
  | "skill"
  | "member"
  | "conversation-search";

export type CommandIndexKind = Exclude<
  CommandKind,
  "page" | "specialist" | "skill" | "conversation-search"
>;

export interface CommandItem {
  id: string;
  kind: CommandKind;
  /** Primary label shown in the result row. */
  label: string;
  /** Secondary line (job title, skill description, nav section). */
  hint?: string;
  /** Path passed to the shell's `onNavigate`. */
  path: string;
  /** Extra haystack tokens beyond label/hint (e.g. raw skill slug). */
  keywords?: string;
}

export interface CommandGroup {
  kind: CommandKind;
  label: string;
  items: CommandItem[];
}

export interface NavSource {
  id: string;
  label: string;
  href: string;
  /**
   * Result-category line for the page row. Resolved from the nav section's rail
   * display label ("Specialist outputs", "Administration"); the primary section has
   * no heading, so its pages carry `undefined` and show no category line.
   */
  section: string | undefined;
}

export interface AgentSource {
  id: string;
  displayName: string;
  jobTitle?: string;
}

export interface SkillSource {
  id: string;
  title: string;
  description?: string;
  /** Absent is read as bundled, matching the catalogue's own default. */
  source?: "bundled" | "catalog";
}

export interface CommandIndexSource {
  id: string;
  kind: CommandIndexKind;
  label: string;
  hint?: string;
  path: string;
  keywords?: string;
}

const GROUP_LABELS: Record<CommandKind, string> = {
  page: "Pages",
  dashboard: "Dashboards",
  application: "Applications",
  workflow: "Workflows",
  blueprint: "Blueprints",
  specialist: "Specialists",
  skill: "Skills",
  member: "Members",
  "conversation-search": "Conversations",
};

// Stable display order for the grouped result list. Pages first so the palette
// doubles as a fast navigation jump even before any data has loaded. For a
// scored query the group holding the best match floats above this order; a tie
// falls back to this index, so Pages still leads when scores are equal.
const GROUP_ORDER: readonly CommandKind[] = [
  "page",
  "dashboard",
  "application",
  "workflow",
  "blueprint",
  "specialist",
  "skill",
  "member",
];

/**
 * Upper bound on how many rows one kind contributes to a scored result set, so
 * a populous kind (dozens of applications) cannot flood the list and bury a
 * better match in another group. 8 keeps the full roster of a seed-sized
 * specialist list visible for a query that matches all of them.
 */
export const MAX_GROUP_RESULTS = 8;

/**
 * Single source of truth for how the search surface is described. The top-bar
 * trigger and the palette input both read from here so they never drift into
 * naming different things; both name the result groups the palette returns.
 */
export const SEARCH_LABEL = "Search conversations, pages, applications, specialists, and skills";
export const SEARCH_PLACEHOLDER = "Search conversations and workspace…";

export function buildCommandItems(input: {
  nav: readonly NavSource[];
  agents: readonly AgentSource[];
  skills: readonly SkillSource[];
  index?: readonly CommandIndexSource[];
}): CommandItem[] {
  const pages: CommandItem[] = input.nav.map((item) => ({
    id: `page:${item.id}`,
    kind: "page",
    label: item.label,
    hint: item.section,
    path: item.href,
  }));

  const specialists: CommandItem[] = input.agents.map((agent) => ({
    id: `specialist:${agent.id}`,
    kind: "specialist",
    label: agent.displayName,
    hint: agent.jobTitle ?? "Specialist",
    path: `/specialists/${agent.id}`,
    keywords: agent.jobTitle,
  }));

  const skills: CommandItem[] = input.skills.map((skill) => ({
    id: `skill:${skill.id}`,
    kind: "skill",
    label: skillDisplayName(skill.title, skill.source),
    hint: skill.description || undefined,
    path: `/skills/${skill.id}`,
    keywords: skill.title,
  }));

  const indexed: CommandItem[] = (input.index ?? []).map((item) => ({
    id: `${item.kind}:${item.id}`,
    kind: item.kind,
    label: item.label,
    hint: item.hint,
    path: item.path,
    keywords: item.keywords,
  }));

  return [...pages, ...indexed, ...specialists, ...skills];
}

/**
 * Case-insensitive subsequence match: every character of the query appears in
 * the haystack in order (so "dsh" matches "Dashboard"). Cheap, dependency-free,
 * and forgiving enough for a small client-side index without pulling in a fuzzy
 * library. Returns false for an empty query so callers can branch on the
 * empty-query state explicitly.
 */
export function fuzzyMatch(query: string, haystack: string): boolean {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return false;
  const h = haystack.toLowerCase();
  let qi = 0;
  for (let hi = 0; hi < h.length && qi < q.length; hi += 1) {
    if (h[hi] === q[qi]) qi += 1;
  }
  return qi === q.length;
}

function words(value: string): string[] {
  return value.split(/[^a-z0-9]+/).filter(Boolean);
}

/**
 * Relevance tier of an item for a non-empty query, higher is better (0 = no
 * match). A conventional ranking — exact, then prefix, then word-prefix, then
 * substring, then loose subsequence — so the best match surfaces regardless of
 * which group it lands in.
 *
 * `keywords` (a slug or job title) matches whole at the exact tier — the
 * keyword exact tier fires only when the entire slug is typed — so an ordinary
 * word inside a job title stays a word-prefix match and does not out-rank a
 * page whose name simply begins with the query.
 *
 * `hint` (a free-text description) participates only at the substring tier and
 * never by subsequence: a long description would otherwise produce spurious
 * subsequence hits from letters that merely happen to fall in order.
 */
export function scoreItem(item: CommandItem, query: string): number {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return 0;
  const label = item.label.toLowerCase();
  const keywords = (item.keywords ?? "").toLowerCase();
  const hint = item.hint?.toLowerCase() ?? "";

  if (label === q || keywords === q) return 5;
  if (label.startsWith(q) || keywords.startsWith(q)) return 4;
  if (
    words(label).some((word) => word.startsWith(q)) ||
    words(keywords).some((word) => word.startsWith(q))
  ) {
    return 3;
  }
  if (label.includes(q) || keywords.includes(q) || (hint !== "" && hint.includes(q))) return 2;
  if (fuzzyMatch(q, `${item.label} ${item.keywords ?? ""}`)) return 1;
  return 0;
}

/**
 * Filters and groups items for display. An empty query returns everything in
 * the fixed group order (no scoring) so the palette opens onto a useful menu of
 * pages + specialists rather than a blank panel. A non-empty query scores every
 * item, drops non-matches, sorts within each group by score then label, caps
 * each group, and floats the group holding the best match to the top.
 */
export function filterCommandItems(items: readonly CommandItem[], query: string): CommandGroup[] {
  const trimmed = query.trim();

  if (trimmed.length === 0) {
    const groups: CommandGroup[] = [];
    for (const kind of GROUP_ORDER) {
      const groupItems = items.filter((item) => item.kind === kind);
      if (groupItems.length > 0) {
        groups.push({ kind, label: GROUP_LABELS[kind], items: groupItems });
      }
    }
    return groups;
  }

  const scored = items
    .map((item) => ({ item, score: scoreItem(item, trimmed) }))
    .filter((entry) => entry.score > 0);

  const ranked: { kind: CommandKind; bestScore: number; order: number; items: CommandItem[] }[] =
    [];
  GROUP_ORDER.forEach((kind, order) => {
    const members = scored
      .filter((entry) => entry.item.kind === kind)
      .sort((a, b) => b.score - a.score || a.item.label.localeCompare(b.item.label))
      .slice(0, MAX_GROUP_RESULTS);
    if (members.length > 0) {
      ranked.push({ kind, bestScore: members[0].score, order, items: members.map((m) => m.item) });
    }
  });

  return ranked
    .sort((a, b) => b.bestScore - a.bestScore || a.order - b.order)
    .map((group) => ({ kind: group.kind, label: GROUP_LABELS[group.kind], items: group.items }));
}

/**
 * The handoff row that carries the typed query into the conversation rail's
 * server-side text search. Its own group, appended after the scored ones by the
 * palette, because it is an action rather than a ranked match: it is offered for
 * every non-empty query, including one nothing in the index matches, which is
 * exactly when searching transcripts is the useful next move.
 */
export function conversationSearchGroup(query: string): CommandGroup | null {
  const trimmed = query.trim();
  if (trimmed.length === 0) return null;
  return {
    kind: "conversation-search",
    label: GROUP_LABELS["conversation-search"],
    items: [
      {
        id: "conversation-search",
        kind: "conversation-search",
        label: `Search conversations for “${trimmed}”`,
        hint: "Match message text across your conversations",
        path: workspaceConversationSearchPath(trimmed),
      },
    ],
  };
}

/** Flattens grouped results back into display order for keyboard navigation. */
export function flattenGroups(groups: readonly CommandGroup[]): CommandItem[] {
  return groups.flatMap((group) => group.items);
}
